import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { OfficeDocumentEditor } from "client/Api/Documents/common";
import { getRelativeEnvironmentUrl } from "client_legacy/ndaw/navigator";
import { deserialize } from "client_legacy/ndaw/mapHelpers";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { safeLocalStorage } from "client_legacy/ndaw/safeStorage";
import { isNil, isNotNil } from "client/Misc/utils";
import { getUserScopedKey } from "client/Hooks/useUserScopedKey";

const STORAGE_KEY = "officePreference";

export function setUserEditorPreference(preference: UserEditorPreference) {
	const storageKey: string = getStorageKey();

	switch (preference) {
		case UserEditorPreference.None:
			safeLocalStorage.removeItem(storageKey);
			break;
		case UserEditorPreference.Local:
			safeLocalStorage.setItem<StoredEditorPreference>(
				storageKey,
				StoredEditorPreference.Local
			);
			break;
		case UserEditorPreference.Online:
			safeLocalStorage.setItem<StoredEditorPreference>(
				storageKey,
				StoredEditorPreference.Online
			);
			break;
		default:
			throw new UnreachableCaseError(preference);
	}
}

export function getUserEditorPreference(): UserEditorPreference {
	const storedPreference: StoredEditorPreference | null | undefined = safeLocalStorage.getItem<
		StoredEditorPreference
	>(getStorageKey());
	if (isNil(storedPreference)) {
		return UserEditorPreference.None;
	}

	try {
		switch (storedPreference) {
			case StoredEditorPreference.Local:
				return UserEditorPreference.Local;
			case StoredEditorPreference.Online:
				return UserEditorPreference.Online;
			default:
				throw new UnreachableCaseError(storedPreference);
		}
	} catch (e) {
		console.error("Something went wrong reading the user editor preference, assuming no preference");
		console.error(e);
		return UserEditorPreference.None;
	}
}

export function computeDefaultEditor(
	environmentDefault: OfficeDocumentEditor,
	userPreference?: UserEditorPreference
): OfficeDocumentEditor {
	let computedDefaultEditor: OfficeDocumentEditor = environmentDefault;

	userPreference ??= getUserEditorPreference();
	switch (userPreference) {
		case UserEditorPreference.None:
			break;
		case UserEditorPreference.Local:
			computedDefaultEditor = OfficeDocumentEditor.Local;
			break;
		case UserEditorPreference.Online:
			computedDefaultEditor = OfficeDocumentEditor.Online;
			break;
		default:
			throw new UnreachableCaseError(userPreference);
	}

	return computedDefaultEditor;
}

export type EditorWithItemFactory<TItem> = [OfficeDocumentEditor, () => TItem | null];

export function getItemsInPreferredOrder<TItem>(
	documentStoreDefault: OfficeDocumentEditor,
	factories: EditorWithItemFactory<TItem>[],
	userPreference?: UserEditorPreference
): TItem[] {
	const result: TItem[] = [];

	const defaultEditor: OfficeDocumentEditor = computeDefaultEditor(documentStoreDefault, userPreference);

	for (const [editor, factory] of factories) {
		const item: TItem | null = factory();
		if (!item) {
			continue;
		}

		if (editor === defaultEditor) {
			result.unshift(item);
		} else {
			result.push(item);
		}
	}

	return result;
}

export async function init(): Promise<void> {
	try {
		const storageKey: string = getStorageKey();

		if (isNotNil(safeLocalStorage.getItem<StoredEditorPreference>(storageKey))) {
			return;
		}

		const legacyCookie: CookieListItem | null = await cookieStore.get(STORAGE_KEY);

		if (
			isNil(legacyCookie?.value)
				|| isNotNil(safeLocalStorage.getItem<StoredEditorPreference>(storageKey))
		) {
			return;
		}

		const legacyPreferences: Map<number, StoredEditorPreference> = deserialize(
			decodeURIComponent(legacyCookie.value)
		);

		for (const [userId, preference] of legacyPreferences) {
			safeLocalStorage.setItem<StoredEditorPreference>(
				getUserScopedKey(userId, STORAGE_KEY),
				preference
			);
		}

		document.cookie = `${STORAGE_KEY}=; path=${getRelativeEnvironmentUrl()}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
	} catch (e) {
		console.error(e);
	}
}

export enum UserEditorPreference {
	None = 0,
	Local = 1,
	Online = 2
}

enum StoredEditorPreference {
	Local = "local",
	Online = "online",
}

function getStorageKey(): string {
	return getUserScopedKey(
		userConfiguration().currentUserIdentity
			.userId,
		STORAGE_KEY
	);
}